import React from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Alert, Button, TextField, Snackbar, CircularProgress } from '@mui/material';
import { sendSms, verifySms } from './Manage2faEndpoints';

export default function Manage2faSmsStep2(props) {
    const { onValidate } = props;
    const dialogSubmit = React.useRef(props.dialogSubmit);
    const [isReady, setIsReady] = React.useState(false);
    const [openConfirmation, setOpenConfirmation] = React.useState(false);

    const [showFormError, setShowFormError] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState(null);
    const [smsStatus, setSmsStatus] = React.useState(false);
    const smsSending = React.useRef(false);
    const initialSmsSent = React.useRef(false);
    const [isLoading, setIsLoading] = React.useState(false);

    const handleCloseConfirmation = (event, reason) => {
        if (reason === 'clickaway') {
          return;
        }

        setOpenConfirmation(false);
    };

    const initialValues = {
        code: '',
        phoneNumber: null
    };

    const validationSchema = yup.object({
        code: yup
            .string()
            .required('Verification Code is required'),
    });

    const sendCode = async () => {
        if (smsSending.current) {
            // already sending don't do anything
            return;
        }
        try {
            smsSending.current = true;
            setIsLoading(true);
            let smsStatusResult = await sendSms(JSON.stringify({phoneNumber: props.phoneNumber}));
            setSmsStatus(smsStatusResult.result);
        }
        catch(err) {
            setOpenConfirmation(false);
        }
        finally {
            setOpenConfirmation(true);
            smsSending.current = false;
            setIsLoading(false);
        }
    }

    if (!initialSmsSent.current)
    {
        sendCode();
        initialSmsSent.current = true;
    }

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                setIsReady(false);
                setShowFormError(false);
                setErrorMessage(null);

                values.phoneNumber = props.phoneNumber;
                await verifySms(values);

                setIsReady(true);
            }
            catch(err) {
                setIsReady(false);
                setShowFormError(true);
                if (err.response && err.response.data) {
                    setErrorMessage(err.response.data.errorMessage);
                }
                else {
                    setErrorMessage("The code is incorrect");
                }
            }
            finally {
                formik.setSubmitting(false);
            }
        },
    });

    const resendButton = () => {
        return (
            <div>
                <Button sx={{pl: 0, textTransform: 'none'}}
                    type="button"
                    disabled={formik.isSubmitting}
                    onClick={sendCode}
                >
                    Re-send code
                </Button>
            </div>
        )
    }

    React.useEffect(() => {
        if (props.dialogSubmit !== dialogSubmit.current) {
            dialogSubmit.current = props.dialogSubmit;
            formik.handleSubmit();
        }

        if (isReady) {
            onValidate(formik?.values);
        }
    }, [formik, isReady, onValidate, props.dialogSubmit]);

    return (
        <div className='ignoreCustomFormStyles'>
            <>
                {showFormError && (
                    <Alert sx={{ mt: 2, mb: 2 }} severity="error">{errorMessage}</Alert>
                )}
                <TextField
                    type="text"
                    name="code"
                    label="Verification Code"
                    fullWidth
                    value={formik.values.code}
                    onChange={formik.handleChange}
                    error={Boolean(formik.errors.code)}
                    helperText={formik.errors.code}
                />
                {isLoading ? (
                    <Alert severity="info" sx={{mt: 2}}>
                        Sending code to {props.phoneNumber}
                        <div>
                        <CircularProgress size="1rem" sx={{mt: 2}} aria-label={"Loading..."} title={"Loading..."} />
                        </div>
                    </Alert>
                ):(
                    <>
                    {smsStatus ? (
                        <Alert severity="success" sx={{mt: 2}}>
                            Successfully sent code to {props.phoneNumber}
                            {resendButton()}
                        </Alert>
                    ):(
                        <Alert severity="error" sx={{mt: 2}}>
                            Failed to send code to {props.phoneNumber}
                            {resendButton()}
                        </Alert>
                    )
                    }
                    </>
                )}
            </>
            <Snackbar
                open={openConfirmation}
                autoHideDuration={6000}
                onClose={handleCloseConfirmation}
            >
                {smsStatus ? (
                    <Alert severity="success">
                        The Verification code was sent.
                    </Alert>
                ) : (
                    <Alert severity="error">
                        The Verification code failed to send.
                    </Alert>
                )}
            </Snackbar>
        </div>
    );
}